/**
 * Codificador de QR Code (ISO/IEC 18004): texto → matriz de módulos. Função pura, sem dependência:
 * modo byte (UTF-8), versões 1 a 40, correção de erro baixa ou média, máscara escolhida pela menor
 * penalidade. É o que o `QrCode` desenha; nada aqui conhece tela, rede ou domínio.
 */

export type QrErrorCorrection = 'low' | 'medium'

const MAX_VERSION = 40

// Tabelas do padrão, por versão (o índice 0 não existe).
const ECC_CODEWORDS_PER_BLOCK: Record<QrErrorCorrection, readonly number[]> = {
  low: [
    -1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30,
    30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30,
  ],
  medium: [
    -1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28,
    28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28,
  ],
}

const ERROR_CORRECTION_BLOCKS: Record<QrErrorCorrection, readonly number[]> = {
  low: [
    -1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14,
    15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25,
  ],
  medium: [
    -1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23,
    25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49,
  ],
}

/** Os dois bits do nível de correção na informação de formato. */
const FORMAT_BITS: Record<QrErrorCorrection, number> = { low: 1, medium: 0 }

function rawDataModules(version: number) {
  let result = (16 * version + 128) * version + 64
  if (version >= 2) {
    const alignments = Math.floor(version / 7) + 2
    result -= (25 * alignments - 10) * alignments - 55
    if (version >= 7) result -= 36
  }
  return result
}

function dataCodewords(version: number, level: QrErrorCorrection) {
  return (
    Math.floor(rawDataModules(version) / 8) -
    ECC_CODEWORDS_PER_BLOCK[level][version]! * ERROR_CORRECTION_BLOCKS[level][version]!
  )
}

/** Multiplicação no corpo GF(2^8) do padrão (polinômio 0x11D). */
function multiply(x: number, y: number) {
  let z = 0
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d)
    z ^= ((y >>> i) & 1) * x
  }
  return z
}

function reedSolomonDivisor(degree: number) {
  const result = new Array<number>(degree).fill(0)
  result[degree - 1] = 1
  let root = 1
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < degree; j++) {
      result[j] = multiply(result[j]!, root)
      if (j + 1 < degree) result[j]! ^= result[j + 1]!
    }
    root = multiply(root, 0x02)
  }
  return result
}

function reedSolomonRemainder(data: readonly number[], divisor: readonly number[]) {
  const result = divisor.map(() => 0)
  for (const byte of data) {
    const factor = byte ^ result.shift()!
    result.push(0)
    divisor.forEach((coefficient, i) => {
      result[i]! ^= multiply(coefficient, factor)
    })
  }
  return result
}

/** Os bytes do texto com o cabeçalho do modo byte, o terminador e o preenchimento da versão. */
function dataBytes(bytes: Uint8Array, version: number, level: QrErrorCorrection) {
  const capacity = dataCodewords(version, level) * 8
  const bits: number[] = []
  const push = (value: number, length: number) => {
    for (let i = length - 1; i >= 0; i--) bits.push((value >>> i) & 1)
  }
  push(0b0100, 4)
  push(bytes.length, version < 10 ? 8 : 16)
  for (const byte of bytes) push(byte, 8)
  push(0, Math.min(4, capacity - bits.length))
  push(0, (8 - (bits.length % 8)) % 8)
  for (let pad = 0xec; bits.length < capacity; pad ^= 0xec ^ 0x11) push(pad, 8)

  const result: number[] = []
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0
    for (let j = 0; j < 8; j++) byte = (byte << 1) | bits[i + j]!
    result.push(byte)
  }
  return result
}

/** Divide em blocos, calcula a correção de cada um e intercala, como o padrão manda gravar. */
function withErrorCorrection(data: readonly number[], version: number, level: QrErrorCorrection) {
  const blockCount = ERROR_CORRECTION_BLOCKS[level][version]!
  const eccLength = ECC_CODEWORDS_PER_BLOCK[level][version]!
  const rawCodewords = Math.floor(rawDataModules(version) / 8)
  const shortBlockCount = blockCount - (rawCodewords % blockCount)
  const shortBlockLength = Math.floor(rawCodewords / blockCount)
  const divisor = reedSolomonDivisor(eccLength)

  const blocks: number[][] = []
  for (let i = 0, k = 0; i < blockCount; i++) {
    const block = data.slice(k, k + shortBlockLength - eccLength + (i < shortBlockCount ? 0 : 1))
    k += block.length
    const ecc = reedSolomonRemainder(block, divisor)
    if (i < shortBlockCount) block.push(0)
    blocks.push(block.concat(ecc))
  }

  const result: number[] = []
  for (let i = 0; i < blocks[0]!.length; i++) {
    blocks.forEach((block, j) => {
      // O byte de enchimento dos blocos curtos não é gravado.
      if (i !== shortBlockLength - eccLength || j >= shortBlockCount) result.push(block[i]!)
    })
  }
  return result
}

function alignmentPositions(version: number, size: number) {
  if (version === 1) return []
  const count = Math.floor(version / 7) + 2
  const step = version === 32 ? 26 : Math.ceil((version * 4 + 4) / (count * 2 - 2)) * 2
  const result = [6]
  for (let position = size - 7; result.length < count; position -= step) {
    result.splice(1, 0, position)
  }
  return result
}

const MASKS: readonly ((x: number, y: number) => boolean)[] = [
  (x, y) => (x + y) % 2 === 0,
  (_x, y) => y % 2 === 0,
  (x) => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
]

/** Penalidade da máscara: sequências, blocos 2×2, falsos padrões de busca e desequilíbrio. */
function penalty(modules: readonly boolean[][]) {
  const size = modules.length
  let result = 0
  const lines: boolean[][] = []
  for (let i = 0; i < size; i++) {
    lines.push(modules[i]!)
    lines.push(modules.map((row) => row[i]!))
  }
  for (const line of lines) {
    let run = 1
    for (let i = 1; i <= size; i++) {
      if (i < size && line[i] === line[i - 1]) run++
      else {
        if (run >= 5) result += 3 + (run - 5)
        run = 1
      }
    }
    // 1:1:3:1:1 escuro com quatro módulos claros de um dos lados.
    const text = line.map((dark) => (dark ? '1' : '0')).join('')
    for (const pattern of ['10111010000', '00001011101']) {
      for (let at = text.indexOf(pattern); at !== -1; at = text.indexOf(pattern, at + 1)) {
        result += 40
      }
    }
  }
  let dark = 0
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const color = modules[y]![x]!
      if (color) dark++
      if (
        x + 1 < size &&
        y + 1 < size &&
        color === modules[y]![x + 1] &&
        color === modules[y + 1]![x] &&
        color === modules[y + 1]![x + 1]
      ) {
        result += 3
      }
    }
  }
  const total = size * size
  result += (Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1) * 10
  return result
}

/**
 * A matriz do código: `true` é módulo escuro, `[linha][coluna]`, sem a margem. Devolve `null`
 * quando o texto não cabe na maior versão (mais de 2.331 bytes em correção média).
 */
export function encodeQr(
  text: string,
  options: { errorCorrection?: QrErrorCorrection; mask?: number } = {},
): boolean[][] | null {
  const level = options.errorCorrection ?? 'medium'
  const bytes = new TextEncoder().encode(text)

  let version = 1
  for (; ; version++) {
    if (version > MAX_VERSION) return null
    const headerBits = 4 + (version < 10 ? 8 : 16)
    if (headerBits + bytes.length * 8 <= dataCodewords(version, level) * 8) break
  }

  const size = version * 4 + 17
  const modules = Array.from({ length: size }, () => new Array<boolean>(size).fill(false))
  const isFunction = Array.from({ length: size }, () => new Array<boolean>(size).fill(false))
  const setFunction = (x: number, y: number, dark: boolean) => {
    modules[y]![x] = dark
    isFunction[y]![x] = true
  }

  const drawFormat = (mask: number) => {
    const data = (FORMAT_BITS[level] << 3) | mask
    let remainder = data
    for (let i = 0; i < 10; i++) remainder = (remainder << 1) ^ ((remainder >>> 9) * 0x537)
    const bits = ((data << 10) | remainder) ^ 0x5412
    const bit = (i: number) => ((bits >>> i) & 1) !== 0
    for (let i = 0; i <= 5; i++) setFunction(8, i, bit(i))
    setFunction(8, 7, bit(6))
    setFunction(8, 8, bit(7))
    setFunction(7, 8, bit(8))
    for (let i = 9; i < 15; i++) setFunction(14 - i, 8, bit(i))
    for (let i = 0; i < 8; i++) setFunction(size - 1 - i, 8, bit(i))
    for (let i = 8; i < 15; i++) setFunction(8, size - 15 + i, bit(i))
    setFunction(8, size - 8, true)
  }

  // Padrões de função: sincronismo, busca, alinhamento, formato e versão.
  for (let i = 0; i < size; i++) {
    setFunction(6, i, i % 2 === 0)
    setFunction(i, 6, i % 2 === 0)
  }
  for (const [cx, cy] of [
    [3, 3],
    [size - 4, 3],
    [3, size - 4],
  ] as const) {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const distance = Math.max(Math.abs(dx), Math.abs(dy))
        const x = cx + dx
        const y = cy + dy
        if (x >= 0 && x < size && y >= 0 && y < size) {
          setFunction(x, y, distance !== 2 && distance !== 4)
        }
      }
    }
  }
  const alignments = alignmentPositions(version, size)
  const last = alignments.length - 1
  alignments.forEach((cx, i) => {
    alignments.forEach((cy, j) => {
      // Os três cantos são dos padrões de busca.
      if ((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0)) return
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          setFunction(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1)
        }
      }
    })
  })
  drawFormat(0)
  if (version >= 7) {
    let remainder = version
    for (let i = 0; i < 12; i++) remainder = (remainder << 1) ^ ((remainder >>> 11) * 0x1f25)
    const bits = (version << 12) | remainder
    for (let i = 0; i < 18; i++) {
      const dark = ((bits >>> i) & 1) !== 0
      const a = size - 11 + (i % 3)
      const b = Math.floor(i / 3)
      setFunction(a, b, dark)
      setFunction(b, a, dark)
    }
  }

  // Os dados, em ziguezague a partir do canto inferior direito.
  const codewords = withErrorCorrection(dataBytes(bytes, version, level), version, level)
  let index = 0
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5
    for (let vertical = 0; vertical < size; vertical++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j
        const upward = ((right + 1) & 2) === 0
        const y = upward ? size - 1 - vertical : vertical
        if (!isFunction[y]![x] && index < codewords.length * 8) {
          modules[y]![x] = ((codewords[index >>> 3]! >>> (7 - (index & 7))) & 1) !== 0
          index++
        }
      }
    }
  }

  const applyMask = (mask: number) => {
    const invert = MASKS[mask]!
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        if (!isFunction[y]![x] && invert(x, y)) modules[y]![x] = !modules[y]![x]
      }
    }
  }

  let mask = options.mask ?? -1
  if (mask < 0 || mask > 7) {
    let lowest = Infinity
    for (let candidate = 0; candidate < 8; candidate++) {
      applyMask(candidate)
      drawFormat(candidate)
      const score = penalty(modules)
      if (score < lowest) {
        lowest = score
        mask = candidate
      }
      applyMask(candidate) // a máscara é um XOR: aplicar de novo desfaz
    }
  }
  applyMask(mask)
  drawFormat(mask)
  return modules
}
